import { useRef, useState } from "react";
import { captionStyle, colors, sans } from "../theme.ts";
import { inputStyle } from "./glass.tsx";

/**
 * One field that edits where it stands: tap the text, change it, and it saves
 * on Enter or when the field loses focus. There is no edit mode and no Save
 * button. Empty saves as null. Escape puts it back.
 */
export function EditLine({
  label, value, placeholder, multiline, onSave,
}: {
  label: string; value: string | null; placeholder: string; multiline?: boolean;
  onSave: (next: string | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  // Escape unmounts the field, and some browsers still raise blur as it goes.
  const open = useRef(false);

  const begin = () => { setDraft(value ?? ""); open.current = true; setEditing(true); };
  const finish = async (keep: boolean) => {
    if (!open.current) return;
    open.current = false;
    setEditing(false);
    const next = draft.trim() || null;
    if (keep && next !== (value ?? null)) await onSave(next);
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={captionStyle}>{label}</div>
      {editing ? (
        multiline ? (
          <textarea
            autoFocus rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
            onBlur={() => finish(true)} onKeyDown={(e) => { if (e.key === "Escape") finish(false); }}
            style={{ ...inputStyle, fontFamily: sans, marginTop: 4, resize: "none" }}
          />
        ) : (
          <input
            autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onBlur={() => finish(true)}
            onKeyDown={(e) => { if (e.key === "Enter") finish(true); if (e.key === "Escape") finish(false); }}
            style={{ ...inputStyle, fontFamily: sans, marginTop: 4 }}
          />
        )
      ) : (
        <div
          onClick={begin} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") begin(); }}
          style={{
            marginTop: 4, minHeight: 24, fontSize: 15, lineHeight: 1.5, cursor: "text", whiteSpace: "pre-wrap",
            color: value ? colors.ink : colors.ink2,
          }}
        >{value ?? placeholder}</div>
      )}
    </div>
  );
}
